import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { GetDashboardReportsAction } from "../../../store/action";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  alertErrorClass,
  spinnerClass,
} from "../dashboardStyles";
import { fromWallClock } from "../../../utils/dates";

const DATE_RANGE_VALUES = ["7days", "30days", "90days", "year"];

/**
 * Status colours are data, not decoration — they key the dots, bars and badges
 * to a booking's state, so they stay as explicit values rather than utilities.
 */
const STATUS_COLORS = {
  completed: "#28a745",
  confirmed: "#5c9f24",
  pending: "#ffc107",
  cancelled: "#dc3545",
};

const getStatusColor = (status) =>
  STATUS_COLORS[status?.toLowerCase()] || "#6f766a";

/** A card wrapper with an icon heading, used by every chart section. */
const SectionCard = ({ icon, title, children, className = "" }) => (
  <div className={`${cardClass} ${className}`}>
    <div className={cardHeaderClass}>
      <h3 className={`${cardTitleClass} flex items-center gap-2`}>
        <i className={`bi ${icon} text-brand-600`} />
        {title}
      </h3>
    </div>
    <div className={cardBodyClass}>{children}</div>
  </div>
);

SectionCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
};

/** Shown wherever a chart has no data to draw. */
const EmptyChart = ({ icon, title, hint }) => (
  <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
    <i className={`bi ${icon} mb-3 text-4xl text-ink-300`} />
    <p className="m-0 text-fluid-sm font-medium text-ink-600">{title}</p>
    {hint && <small className="mt-1 text-xs text-ink-400">{hint}</small>}
  </div>
);

EmptyChart.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
};

/** One headline figure with its trend line. */
const MetricCard = ({ icon, iconColor, value, label, trend, trendLabel }) => {
  const direction = trend > 0 ? "up" : trend < 0 ? "down" : "flat";
  const trendStyles = {
    up: "text-brand-700",
    down: "text-red-600",
    flat: "text-ink-400",
  };
  const trendIcons = {
    up: "bi-arrow-up",
    down: "bi-arrow-down",
    flat: "bi-dash",
  };

  return (
    <div
      className={`${cardClass} flex items-start gap-4 p-5 transition hover:shadow-card-hover`}
    >
      <div
        className="flex size-11 shrink-0 items-center justify-center rounded-lg text-white"
        style={{ background: iconColor }}
      >
        <i className={`bi ${icon} text-xl`} />
      </div>
      <div className="min-w-0">
        <h4 className="m-0 text-2xl font-bold text-ink-900">{value}</h4>
        <p className="m-0 text-xs text-ink-500">{label}</p>
        <span
          className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold ${trendStyles[direction]}`}
        >
          <i className={`bi ${trendIcons[direction]}`} />
          {trendLabel}
        </span>
      </div>
    </div>
  );
};

MetricCard.propTypes = {
  icon: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  trend: PropTypes.number,
  trendLabel: PropTypes.string.isRequired,
};

/**
 * Reports Page - Analytics and business insights
 * Displays comprehensive booking analytics for the organization
 */
const Reports = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const formatStatus = (status) =>
    t(`Dashboard.Bookings.status.${status?.toLowerCase()}`, { defaultValue: status });
  const DATE_RANGES = DATE_RANGE_VALUES.map((value) => ({
    value,
    label: t(`Dashboard.Reports.dateRanges.${value}`),
  }));
  const [dateRange, setDateRange] = useState("30days");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState(null);
  const [planLocked, setPlanLocked] = useState(false);

  // Fetch reports data
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPlanLocked(false);
      const data = await GetDashboardReportsAction(dateRange);
      setReports(data);
    } catch (err) {
      console.error("Error fetching reports:", err);
      // 403 means the account has no organization yet, which is a normal state
      // mid-onboarding rather than a failure worth alarming the user about.
      if (err.response?.status === 403) {
        setReports(null);
        setError(null);
      } else if (err.response?.status === 402) {
        // Analytics isn't on this plan — offer an upgrade rather than an error.
        setReports(null);
        setError(null);
        setPlanLocked(true);
      } else {
        setError(t("Dashboard.Reports.loadError"));
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Format percentage with sign. Defaults to 0 so a missing trend renders "0%"
  // rather than the literal "undefined%".
  const formatTrend = (value) => {
    const n = Number(value) || 0;
    return `${n > 0 ? "+" : ""}${n}%`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
  };

  // A day with bookings gets at least a visible stub; a day without gets no bar
  // at all, only the baseline. A minimum-height green bar on an empty day looked
  // exactly like a day with a booking.
  const getBarHeight = (count, maxCount) =>
    count > 0 && maxCount > 0 ? `${Math.max(5, (count / maxCount) * 100)}%` : "2px";

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.Reports.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  // Analytics is a paid feature. On the free tier the API answers 402 and we
  // show an upgrade prompt instead of the (empty) dashboard.
  if (planLocked) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center rounded-card border border-ink-200 bg-white p-8 text-center shadow-card">
        <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-brand-50 text-2xl text-brand-600">
          <i className="bi bi-bar-chart-line" aria-hidden="true" />
        </div>
        <h2 className="m-0 text-xl font-bold text-ink-900">
          {t("Dashboard.Reports.upgradeTitle")}
        </h2>
        <p className="mt-2 max-w-md text-fluid-sm text-ink-500">
          {t("Dashboard.Reports.upgradeBody")}
        </p>
        <a href="/dashboard/settings" className={`${btnPrimaryClass} mt-5`}>
          <i className="bi bi-arrow-up-circle" aria-hidden="true" />
          {t("Dashboard.Reports.upgradeCta")}
        </a>
      </div>
    );
  }

  const summary = reports?.summary || {};
  const trends = summary?.trends || {};

  // The API sends a day for every date in the period (from the day the business
  // was created), bookings or not, so "is there anything to chart" is a count,
  // not a length.
  const bookingsByDay = reports?.bookingsByDay || [];
  const maxDayCount = Math.max(0, ...bookingsByDay.map((d) => d.count));

  return (
    <div className="flex flex-col gap-5">
      {/* Header with Gradient */}
      <div className="rounded-card bg-linear-to-r from-brand-700 to-brand-500 px-5 py-6 shadow-brand sm:px-6">
        <h1 className="m-0 text-2xl font-bold text-white sm:text-3xl">
          {t("Dashboard.Reports.title")}
        </h1>
        <p className="mt-1 flex items-center gap-2 text-fluid-sm text-brand-50">
          <span className="inline-block size-2 shrink-0 animate-pulse rounded-full bg-brand-100 motion-reduce:animate-none" />
          {t("Dashboard.Reports.subtitle")}
        </p>
      </div>

      {error && (
        <div className={`${alertErrorClass} mb-0`} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            className="shrink-0 cursor-pointer rounded p-0.5 text-red-700 transition hover:bg-red-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            onClick={() => setError(null)}
            aria-label={t("Dashboard.Reports.dismissError")}
          >
            <i className="bi bi-x text-lg" />
          </button>
        </div>
      )}

      {/* Date Range Selector */}
      <div className={cardClass}>
        <div className={cardBodyClass}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold text-ink-600">
              {t("Dashboard.Reports.timePeriod")}
            </span>
            {DATE_RANGES.map((option) => (
              <button
                key={option.value}
                className={
                  dateRange === option.value
                    ? btnPrimaryClass
                    : btnSecondaryClass
                }
                aria-pressed={dateRange === option.value}
                onClick={() => setDateRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))]">
        <MetricCard
          icon="bi-calendar-check"
          iconColor="var(--color-brand-600)"
          value={summary.totalBookings || 0}
          label={t("Dashboard.Reports.metrics.totalBookings")}
          trend={trends.bookingsChange}
          trendLabel={t("Dashboard.Reports.metrics.vsPrevious", {
            value: formatTrend(trends.bookingsChange),
          })}
        />
        <MetricCard
          icon="bi-check-circle"
          iconColor="#28a745"
          value={summary.completedBookings || 0}
          label={t("Dashboard.Reports.metrics.completed")}
          trend={trends.completedChange}
          trendLabel={t("Dashboard.Reports.metrics.vsPrevious", {
            value: formatTrend(trends.completedChange),
          })}
        />
        <MetricCard
          icon="bi-people"
          iconColor="#ffc107"
          value={summary.uniqueCustomers || 0}
          label={t("Dashboard.Reports.metrics.uniqueCustomers")}
          trend={trends.customersChange}
          trendLabel={t("Dashboard.Reports.metrics.vsPrevious", {
            value: formatTrend(trends.customersChange),
          })}
        />
        <MetricCard
          icon="bi-graph-up"
          iconColor="#6f42c1"
          value={`${summary.completionRate || 0}%`}
          label={t("Dashboard.Reports.metrics.completionRate")}
          trend={0}
          trendLabel={t("Dashboard.Reports.metrics.bookingsPerDay", {
            value: summary.averageBookingsPerDay || 0,
          })}
        />
      </div>

      {/* Quick Stats Row */}
      <div className="flex flex-wrap gap-3">
        {[
          {
            icon: "bi-briefcase",
            text: t("Dashboard.Reports.quickStats.activeServices", {
              count: summary.activeServices || 0,
            }),
          },
          {
            icon: "bi-person-badge",
            text: t("Dashboard.Reports.quickStats.resources", {
              count: summary.activeResources || 0,
            }),
          },
          {
            icon: "bi-hourglass-split",
            text: t("Dashboard.Reports.quickStats.pending", {
              count: summary.pendingBookings || 0,
            }),
          },
          {
            icon: "bi-x-circle",
            text: t("Dashboard.Reports.quickStats.cancellationRate", {
              value: summary.cancellationRate || 0,
            }),
          },
        ].map((stat) => (
          <div
            key={stat.text}
            className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3.5 py-2 text-xs font-medium text-ink-600 shadow-card"
          >
            <i className={`bi ${stat.icon} text-brand-600`} />
            <span>{stat.text}</span>
          </div>
        ))}
      </div>

      {/* Bookings Overview Chart */}
      <SectionCard
        icon="bi-graph-up"
        title={t("Dashboard.Reports.bookingsOverview.title")}
      >
        {maxDayCount > 0 ? (
          <div>
            <div className="flex h-64 items-end gap-1 overflow-x-auto pb-2">
              {bookingsByDay.map((day, index) => {
                return (
                  <div
                    key={index}
                    className="flex h-full min-w-8 flex-1 flex-col items-center gap-1.5 pt-5"
                  >
                    <div className="flex min-h-0 w-full flex-1 items-end justify-center">
                      <div
                        className={`group relative flex w-full max-w-10 justify-center rounded-t-md transition ${
                          day.count > 0
                            ? "bg-brand-600 hover:bg-brand-500"
                            : "bg-ink-200"
                        }`}
                        style={{
                          height: getBarHeight(day.count, maxDayCount),
                        }}
                        title={t("Dashboard.Reports.bookingsOverview.barTooltip", {
                          date: formatDate(day.date),
                          count: day.count,
                        })}
                      >
                        {day.count > 0 && (
                          <span className="-mt-5 text-[0.625rem] font-semibold text-ink-600">
                            {day.count}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[0.625rem] whitespace-nowrap text-ink-400">
                      {formatDate(day.date)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex justify-center border-t border-ink-200 pt-3">
              <span className="flex items-center gap-1.5 text-xs text-ink-500">
                <i className="bi bi-circle-fill text-brand-600" />
                {t("Dashboard.Reports.bookingsOverview.totalBookings")}
              </span>
            </div>
          </div>
        ) : (
          <EmptyChart
            icon="bi-graph-up-arrow"
            title={t("Dashboard.Reports.bookingsOverview.emptyTitle")}
            hint={t("Dashboard.Reports.bookingsOverview.emptyHint")}
          />
        )}
      </SectionCard>

      {/* Two Column Layout */}
      <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(340px,100%),1fr))]">
        {/* Bookings by Status */}
        <SectionCard
          icon="bi-pie-chart-fill"
          title={t("Dashboard.Reports.bookingsByStatus.title")}
        >
          {reports?.bookingsByStatus?.length > 0 ? (
            <div className="flex flex-col gap-4">
              {reports.bookingsByStatus.map((status, index) => (
                <div key={index} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-fluid-sm font-medium text-ink-700 capitalize">
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ background: getStatusColor(status.status) }}
                      />
                      {formatStatus(status.status)}
                    </span>
                    <span className="text-sm font-semibold text-ink-900">
                      {status.count}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100">
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{
                          width: `${status.percentage}%`,
                          background: getStatusColor(status.status),
                        }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-xs text-ink-500">
                      {status.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart
              icon="bi-pie-chart"
              title={t("Dashboard.Reports.bookingsByStatus.emptyTitle")}
            />
          )}
        </SectionCard>

        {/* Peak Hours */}
        <SectionCard
          icon="bi-clock-fill"
          title={t("Dashboard.Reports.peakHours.title")}
        >
          {reports?.peakHours?.some((h) => h.count > 0) ? (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(3.5rem,1fr))] gap-1.5">
              {reports.peakHours
                .filter((_, i) => i >= 8 && i <= 20)
                .map((hour, index) => {
                  const maxCount = Math.max(
                    ...reports.peakHours.map((h) => h.count),
                  );
                  const intensity =
                    maxCount > 0 ? (hour.count / maxCount) * 100 : 0;
                  return (
                    <div
                      key={index}
                      className={`flex flex-col items-center gap-0.5 rounded-md border border-ink-200 px-1 py-2 ${
                        intensity > 55 ? "text-white" : "text-ink-600"
                      }`}
                      style={{
                        background: `rgba(92, 159, 36, ${intensity / 100})`,
                      }}
                      title={t("Dashboard.Reports.peakHours.barTooltip", {
                        time: new Date(2026, 0, 1, hour.hour).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
                        count: hour.count,
                      })}
                    >
                      <span className="text-[0.625rem] whitespace-nowrap">
                        {new Date(2026, 0, 1, hour.hour).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="text-sm font-bold">{hour.count}</span>
                    </div>
                  );
                })}
            </div>
          ) : (
            <EmptyChart
              icon="bi-clock-history"
              title={t("Dashboard.Reports.peakHours.emptyTitle")}
            />
          )}
        </SectionCard>
      </div>

      {/* Bookings by Day of Week */}
      <SectionCard
        icon="bi-calendar-week"
        title={t("Dashboard.Reports.bookingsByDayOfWeek.title")}
      >
        {reports?.bookingsByDayOfWeek?.some((d) => d.count > 0) ? (
          <div className="flex flex-col gap-2.5">
            {reports.bookingsByDayOfWeek.map((day, index) => {
              const maxCount = Math.max(
                ...reports.bookingsByDayOfWeek.map((d) => d.count),
              );
              return (
                <div key={index} className="flex items-center gap-3">
                  <span className="w-9 shrink-0 text-xs font-semibold text-ink-600">
                    {new Date(2026, 0, 4 + day.dayOfWeek).toLocaleDateString(locale, { weekday: "short" })}
                  </span>
                  <div className="h-6 flex-1 overflow-hidden rounded-md bg-ink-100">
                    <div
                      className="flex h-full items-center justify-end rounded-md bg-brand-600 pr-2 transition-[width] duration-500"
                      style={{ width: `${getBarHeight(day.count, maxCount)}%` }}
                    >
                      <span className="text-[0.625rem] font-semibold text-white">
                        {day.count}
                      </span>
                    </div>
                  </div>
                  <span className="w-10 shrink-0 text-right text-xs text-ink-500">
                    {day.percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyChart
            icon="bi-calendar-week"
            title={t("Dashboard.Reports.bookingsByDayOfWeek.emptyTitle")}
          />
        )}
      </SectionCard>

      {/* Top Services & Resources */}
      <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(340px,100%),1fr))]">
        <SectionCard
          icon="bi-trophy-fill"
          title={t("Dashboard.Reports.topServices.title")}
        >
          {reports?.topServices?.length > 0 ? (
            <div className="flex flex-col gap-2">
              {reports.topServices.map((service, index) => (
                <TopItem
                  key={index}
                  rank={index + 1}
                  name={service.serviceName}
                  percentage={service.percentage}
                  stats={[
                    {
                      text: t("Dashboard.Reports.topServices.bookingsCount", {
                        count: service.bookingCount,
                      }),
                    },
                    {
                      text: t("Dashboard.Reports.topServices.completedCount", {
                        count: service.completedCount,
                      }),
                      className: "text-brand-700",
                    },
                  ]}
                />
              ))}
            </div>
          ) : (
            <EmptyChart
              icon="bi-briefcase"
              title={t("Dashboard.Reports.topServices.emptyTitle")}
            />
          )}
        </SectionCard>

        <SectionCard
          icon="bi-star-fill"
          title={t("Dashboard.Reports.topResources.title")}
        >
          {reports?.topResources?.length > 0 ? (
            <div className="flex flex-col gap-2">
              {reports.topResources.map((resource, index) => (
                <TopItem
                  key={index}
                  rank={index + 1}
                  name={resource.resourceName}
                  percentage={resource.percentage}
                  stats={[
                    {
                      text: t("Dashboard.Reports.topResources.bookingsCount", {
                        count: resource.bookingCount,
                      }),
                    },
                    { text: t(`Dashboard.Resources.form.type${resource.resourceType}`, { defaultValue: resource.resourceType }), className: "text-ink-400" },
                  ]}
                />
              ))}
            </div>
          ) : (
            <EmptyChart
              icon="bi-person-badge"
              title={t("Dashboard.Reports.topResources.emptyTitle")}
            />
          )}
        </SectionCard>
      </div>

      {/* Recent Activity */}
      <SectionCard
        icon="bi-activity"
        title={t("Dashboard.Reports.recentActivity.title")}
      >
        {reports?.recentActivity?.length > 0 ? (
          <div className="flex flex-col gap-2">
            {reports.recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-ink-200 p-3 transition hover:bg-ink-50"
              >
                <div
                  className="flex size-9 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ background: getStatusColor(activity.status) }}
                >
                  <i className="bi bi-calendar-check" />
                </div>
                <div className="min-w-40 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-fluid-sm font-semibold text-ink-900">
                      {activity.customerName}
                    </span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[0.625rem] font-semibold text-white capitalize"
                      style={{ background: getStatusColor(activity.status) }}
                    >
                      {formatStatus(activity.status)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-500">
                    <span className="flex items-center gap-1">
                      <i className="bi bi-briefcase" /> {activity.serviceName}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="bi bi-person" /> {activity.resourceName}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="bi bi-calendar" />{" "}
                      {fromWallClock(activity.bookingDateTime).toLocaleDateString(locale)}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 rounded-md bg-ink-100 px-2 py-1 font-mono text-xs text-ink-600">
                  {activity.confirmationCode}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyChart
            icon="bi-inbox"
            title={t("Dashboard.Reports.recentActivity.emptyTitle")}
          />
        )}
      </SectionCard>
    </div>
  );
};

/** A ranked row in the Top Services / Top Resources lists. */
const TopItem = ({ rank, name, stats, percentage }) => (
  <div className="flex items-center gap-3 rounded-lg border border-ink-200 p-3 transition hover:bg-ink-50">
    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-brand-700">
      {rank}
    </div>
    <div className="min-w-0 flex-1">
      <span className="block truncate text-fluid-sm font-medium text-ink-900">
        {name}
      </span>
      <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-500">
        {stats.map((stat) => (
          <span key={stat.text} className={stat.className}>
            {stat.text}
          </span>
        ))}
      </div>
    </div>
    <div className="shrink-0 text-sm font-bold text-brand-700">
      {percentage}%
    </div>
  </div>
);

TopItem.propTypes = {
  rank: PropTypes.number.isRequired,
  name: PropTypes.string,
  stats: PropTypes.arrayOf(
    PropTypes.shape({ text: PropTypes.string, className: PropTypes.string }),
  ).isRequired,
  percentage: PropTypes.number,
};

export default Reports;
